import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { EditableText } from './inline/EditableText';
import { EditableRichText } from './inline/EditableRichText';

// Default content (fallback if CMS data not available)
const defaultContent = {
  title: "World Nuclear Congress",
  subtitle: "The Independent U.S. Forum for Global Nuclear Progress",
  main_content: "The World Nuclear Congress (WNC) is an independent, U.S.-based international forum convened by Congressional Energy Engagement (CEE). The Congress brings together U.S. leadership, allied nations, and international partners—including embassy representatives, policymakers, industry leaders, national laboratory experts, and academic institutions—to shape the future of nuclear energy in a rapidly evolving global landscape.",
  expanded_content: `By intentionally bringing global perspectives into a U.S. setting, the World Nuclear Congress "brings the world home," creating a forum where internationally experienced leaders, global experts, and U.S. stakeholders alike engage directly in the conversations shaping nuclear policy, deployment, and security. Reflecting CEE's core belief that "human achievement is inseparable from human interaction," the Congress is designed to foster trust, direct engagement, meaningful exchange and forward momentum.

Through this intentional approach, the World Nuclear Congress elevates understanding of how allied relationships underpin safe, secure, and timely nuclear deployment—both within the United States and abroad. By strengthening cooperation across policy, innovation, and implementation, the Congress advances shared solutions that enhance energy security, support global stability, and reinforce the collective responsibility of the United States and its allies in building a safer, more resilient world—an environment where ideas are advanced through conversation, relationships are strengthened, and shared objectives take form.`,
  closing_statement: "Join us in shaping the next chapter of global nuclear leadership—through collaboration, partnership, and shared purpose.",
  why_attend_title: "Why Attend",
  why_attend_content: "The World Nuclear Congress is designed for leaders who value meaningful exchange and forward momentum. Attendees participate in substantive conversations that connect policy with deployment, innovation with readiness, and national priorities with global partnership—creating opportunities to build relationships, gain perspective, and contribute to outcomes that extend well beyond the Congress itself."
};

const AboutSection = ({ content, sectionId }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Merge CMS content with defaults
  const data = { ...defaultContent, ...content };
  const scope = (path) => ({ type: 'homepage_section', sectionId, path });

  // Split expanded content into paragraphs
  const expandedParagraphs = data.expanded_content
    ? data.expanded_content.split('\n\n').filter(p => p.trim())
    : [];

  return (
    <section id="about-section" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Main Description */}
        <div className="mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#00457f] mb-8 text-center">
            <EditableText as="span" scope={scope('title')} value={data.title} multiline={false}>{data.title}</EditableText>
          </h2>
          
          <div className="space-y-6 text-lg text-gray-700 leading-relaxed max-w-5xl mx-auto">
            {/* First paragraph - always visible */}
            <p>
              <EditableText as="span" scope={scope('main_content')} value={data.main_content}>{data.main_content}</EditableText>
            </p>
            
            {/* Expandable content */}
            {expandedParagraphs.length > 0 && (
              <div className={`space-y-6 overflow-hidden transition-all duration-500 ${isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                {expandedParagraphs.map((paragraph, idx) => (
                  <p key={idx}>{paragraph}</p>
                ))}
              </div>
            )}

            {/* Expand/Collapse Button */}
            {expandedParagraphs.length > 0 && (
              <div className="flex justify-center pt-4">
                <Button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="bg-gradient-to-r from-yellow-500 via-amber-500 to-yellow-600 hover:from-yellow-600 hover:via-amber-600 hover:to-yellow-700 text-white font-bold text-lg px-8 py-6 rounded-full transition-all duration-300 transform hover:scale-105 shadow-xl"
                  style={{
                    boxShadow: '0 10px 40px rgba(245, 158, 11, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                  }}
                >
                  {isExpanded ? (
                    <>
                      Read Less
                      <ChevronUp className="ml-2 w-5 h-5" />
                    </>
                  ) : (
                    <>
                      Read More
                      <ChevronDown className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Closing statement - always visible below button */}
            {data.closing_statement && (
              <p className="font-semibold text-[#00457f] text-xl text-center pt-6">
                <EditableText as="span" scope={scope('closing_statement')} value={data.closing_statement}>{data.closing_statement}</EditableText>
              </p>
            )}
          </div>
        </div>

        {/* Why Attend Section */}
        {data.why_attend_content && (
          <div className="bg-gradient-to-br from-[#00457f] to-[#003461] rounded-3xl p-10 md:p-12 shadow-2xl">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-6 text-center">
              <EditableText as="span" scope={scope('why_attend_title')} value={data.why_attend_title} multiline={false}>{data.why_attend_title || "Why Attend"}</EditableText>
            </h3>
            <p className="text-lg md:text-xl text-gray-200 leading-relaxed text-center max-w-4xl mx-auto">
              <EditableText as="span" scope={scope('why_attend_content')} value={data.why_attend_content}>{data.why_attend_content}</EditableText>
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default AboutSection;
